/**
 * Cross-host redirect validation + auth-link builders.
 *
 * `getValidCrossHostRedirect` widens `getValidRedirect` to also accept absolute
 * URLs whose host is the LOCI eTLD+1 (`loci.city` in prod, `loci.localhost` in
 * dev, plus the staging variant). Used by:
 *   - `AuthContextRTK.login` to honor `?next=https://clt.loci.city/...`.
 *   - `crossHostLogin` / `crossHostSignup` to sanitize the inbound `next` before
 *     embedding it in the outbound app-host URL — defense in depth so a forged
 *     link from a phishing site can't smuggle a `next` past us.
 *
 * Reads `NEXT_PUBLIC_LOCI_ROOT_DOMAIN` (default `loci.city`) and
 * `NEXT_PUBLIC_LOCI_STAGE` directly from `process.env` — same convention as
 * `lib/cross-host-urls.ts`. Stays browser-safe and avoids the `@/config/env`
 * Storybook bundling problem (APO-207).
 */

import { appUrl, type CrossHostStageOptions } from '@/lib/cross-host-urls';
import { getValidRedirect } from '@/lib/redirectValidation';

function rootDomain(): string {
  return process.env.NEXT_PUBLIC_LOCI_ROOT_DOMAIN ?? 'loci.city';
}

/**
 * The root domain WITHOUT a port.
 *
 * `NEXT_PUBLIC_LOCI_ROOT_DOMAIN` conflates two things: `lib/cross-host-urls.ts`
 * needs an *authority* to build absolute links, so local dev must set
 * `loci.localhost:3000` (see `apps/web/.env.example`) or every cross-host link
 * points at port 80. `isLociHost` needs a *domain*, because it compares against
 * `URL.hostname`, which never carries a port.
 *
 * APO-402: with the documented local value, that mismatch made
 * `'clt.loci.localhost'.endsWith('.loci.localhost:3000')` false, so EVERY
 * cross-host `next` was silently rejected in dev — `crossHostLogin(
 * 'http://clt.loci.localhost:3000/venues')` returned a bare
 * `http://app.loci.localhost:3000/login` with the destination dropped. Prod was
 * unaffected (`loci.city` carries no port), which is exactly why it survived:
 * the one suite that would have caught it, `cross-host-auth.spec.ts`, was
 * gated behind an env var no workflow set.
 *
 * Stripping the port narrows nothing — the candidate is still required to be
 * the LOCI eTLD+1 or a subdomain of it.
 */
function rootHostname(): string {
  const root = rootDomain();
  const portIndex = root.lastIndexOf(':');
  return portIndex === -1 ? root : root.slice(0, portIndex);
}

function isLociHost(hostname: string): boolean {
  const root = rootHostname();
  // `host === root` (apex) OR `host.endsWith('.' + root)` (subdomain). The
  // dot prefix prevents `notloci.city` / `loci.city.evil.com` from matching.
  return hostname === root || hostname.endsWith(`.${root}`);
}

/**
 * Validate a redirect target that may legitimately be either a same-origin
 * path or an absolute URL within the LOCI eTLD+1.
 *
 * Returns `fallback` for: `null`, malformed input, non-LOCI hosts, protocol-
 * relative URLs, non-http(s) schemes, or anything that fails the same-origin
 * path rules in `getValidRedirect`.
 */
export function getValidCrossHostRedirect(
  candidate: string | null | undefined,
  fallback: string = '/',
): string {
  if (!candidate) return fallback;

  // Same-origin path? Delegate to the existing helper unchanged.
  if (candidate.startsWith('/')) {
    const validated = getValidRedirect(candidate, '__INVALID__');
    return validated === '__INVALID__' ? fallback : validated;
  }

  // Absolute URL — must parse, must be http(s), must be on a LOCI host.
  let parsed: URL;
  try {
    parsed = new URL(candidate);
  } catch {
    return fallback;
  }
  if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return fallback;
  if (!isLociHost(parsed.hostname)) return fallback;
  return candidate;
}

/** Options for the cross-host auth-link builders. */
export interface CrossHostAuthOptions extends CrossHostStageOptions {
  /**
   * Append the FRE-784 stale-cookie escape hatch (`?force=1`). The proxy treats
   * a request as authenticated on cookie *presence* alone (no Edge JWT decoder),
   * so a returning visitor whose access/refresh cookie is expired-but-present is
   * otherwise bounced off `/login` straight back to the feed. Sign-in controls
   * render only when the client believes it is signed out, so forcing the form
   * is always the intended outcome there — set this on those links.
   */
  readonly force?: boolean;
}

/**
 * Build a cross-host auth URL (`app.loci.city/<path>?next=<next>`). Shared by
 * `crossHostLogin` / `crossHostSignup` so the `next` sanitization and the
 * `?force=1` escape-hatch handling stay identical across both surfaces.
 *
 * `next` is sanitized via `getValidCrossHostRedirect` before being embedded.
 * `options.force` appends `?force=1` via `URL` so a present `?next=` is
 * preserved rather than producing a malformed `?next=…?force=1`.
 */
function buildCrossHostAuthUrl(
  path: '/login' | '/signup',
  next?: string | null,
  options?: CrossHostAuthOptions,
): string {
  const validated = next ? getValidCrossHostRedirect(next, '__INVALID__') : null;
  const safeNext = validated && validated !== '__INVALID__' ? validated : undefined;
  const base = appUrl({
    city: null,
    path,
    next: safeNext,
    isStaging: options?.isStaging,
    host: options?.host,
  });
  if (!options?.force) return base;
  const url = new URL(base);
  url.searchParams.set('force', '1');
  return url.toString();
}

/**
 * Build the canonical cross-host login URL: `app.loci.city/login?next=<next>`.
 *
 * Callers can pass either a same-origin path (`/event/foo`) or an absolute
 * cross-host URL (`https://clt.loci.city/event/foo`); both are accepted. Pass
 * `{ force: true }` to append `?force=1` (see `CrossHostAuthOptions`).
 */
export function crossHostLogin(next?: string | null, options?: CrossHostAuthOptions): string {
  return buildCrossHostAuthUrl('/login', next, options);
}

/** Sibling builder for the signup surface — same validation + options as `crossHostLogin`. */
export function crossHostSignup(next?: string | null, options?: CrossHostAuthOptions): string {
  return buildCrossHostAuthUrl('/signup', next, options);
}

/**
 * Navigate to a post-auth `target` (already sanitized by
 * `getValidCrossHostRedirect`) with the correct mechanism: cross-host absolute
 * URLs need a hard navigation (`window.location.assign`) so the destination host
 * loads a fresh document; same-host paths route through the App Router so client
 * state survives.
 *
 * Single source of truth for the assign-vs-push split shared by
 * `AuthContextRTK.login` / `.register` and the signup page's post-signup
 * navigation — so any future protocol or validation change lives in one place.
 * The router is typed structurally (only `push` is used) to keep this module
 * free of a `next/navigation` runtime import.
 */
export function navigateToValidTarget(
  router: { push: (href: string) => void },
  target: string,
): void {
  if (target.startsWith('http://') || target.startsWith('https://')) {
    window.location.assign(target);
  } else {
    router.push(target);
  }
}
